import React, { useReducer, useEffect, useState } from "react";
import { Routes, Route, Navigate, useParams, useNavigate, Outlet } from "react-router-dom";
import FlexiOwnPersonalDetailsV2 from "./FlexiOwnPersonalDetailsV2";
import FlexiOwnAddressDetailsV2 from "./FlexiOwnAddressDetailsV2";
import FlexiOwnDriverLicenceV2 from "./FlexiOwnDriverLicenceV2";
import FlexiOwnEmergencyContactsV2 from "./FlexiOwnEmergencyContactsV2";
import FlexiOwnAgreementScheduleV2 from "./FlexiOwnAgreementScheduleV2";
import FlexiOwnInsurancePriceBeatV2 from "./FlexiOwnInsurancePriceBeatV2";
import FlexiOwnOnboardingReviewV2 from "./FlexiOwnOnboardingReviewV2";
import FlexiOwnUsedVehicleAddendumV2 from "./FlexiOwnUsedVehicleAddendumV2";
import FlexiOwnNSWEVAddendumV2 from "./FlexiOwnNSWEVAddendumV2";
import FlexiOwnUberProofV2 from "./FlexiOwnUberProofV2";
import { onboardingPersonalReducer, onboardingPersonalInitialState } from "./onboardingPersonalReducer";
import { onboardingAddressReducer, onboardingAddressInitialState } from "./onboardingAddressReducer";
import { onboardingDriverLicenceReducer, onboardingDriverLicenceInitialState } from "./onboardingDriverLicenceReducer";
import { onboardingEmergencyContactsReducer, onboardingEmergencyContactsInitialState } from "./onboardingEmergencyContactsReducer";
import { onboardingAgreementReducer, onboardingAgreementInitialState } from "./onboardingAgreementReducer";
import { onboardingInsuranceReducer, onboardingInsuranceInitialState } from "./onboardingInsuranceReducer";
import { onboardingVehicleReducer, onboardingVehicleInitialState } from "./onboardingVehicleReducer";
import Warning from "@components/modal/info/Warning";
import { Country } from "@utils/constants/localisation";
import useServiceHook from '@hooks/useServiceHook';
import customerDetails from '@services/customer/CustomerDetails';
import InvoicePreviewV2 from "./InvoicePreviewV2";
import { useOnboardingData } from "@hooks/useOnboardingData";
import { getTotalWeeklySubscriptionPrice } from "@utils/pricingRecordHelpers";
import { CBPAllowedPlans } from "@utils/planOptions";
import { flexiOwnUsedPlanNames, preLovedPlanNames } from "@utils/constants/products";

// Unified enum — superset of AU and GB journey paths
export enum FlexiOwnOnboardingPath {
  Root = "",
  UberProof = "uber-proof",                    // AU only
  PersonalDetails = "personal-details",
  AddressDetails = "address-details",
  DriverLicence = "driver-licence",
  EmergencyContacts = "emergency-contacts",
  AgreementSchedule = "agreement-schedule",
  UsedVehicleAddendum = "used-vehicle-addendum",
  NSWEVAddendum = "nsw-ev-addendum",           // AU only
  InsurancePriceBeat = "insurance-price-beat",
  Review = "review",
  InvoicePreview = "invoice-preview",
}

export default function OnboardingParent() {
  const { custId, country: countryParam } = useParams();
  const navigate = useNavigate();

  const country = countryParam as Country;
  const isAU = country === Country.AU;

  const [onboardingPersonalState, onboardingPersonalDispatch] = useReducer(onboardingPersonalReducer, onboardingPersonalInitialState);
  const [onboardingAddressState, onboardingAddressDispatch] = useReducer(onboardingAddressReducer, onboardingAddressInitialState);
  const [onboardingDriverLicenceState, onboardingDriverLicenceDispatch] = useReducer(onboardingDriverLicenceReducer, onboardingDriverLicenceInitialState);
  const [onboardingEmergencyContactsState, onboardingEmergencyContactsDispatch] = useReducer(onboardingEmergencyContactsReducer, onboardingEmergencyContactsInitialState);
  const [onboardingAgreementState, onboardingAgreementDispatch] = useReducer(onboardingAgreementReducer, onboardingAgreementInitialState);
  const [onboardingInsuranceState, onboardingInsuranceDispatch] = useReducer(onboardingInsuranceReducer, onboardingInsuranceInitialState);
  const [onboardingVehicleState, onboardingVehicleDispatch] = useReducer(onboardingVehicleReducer, onboardingVehicleInitialState);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Unknown error");

  const [loading, error, errMessage, customerDetailsData] = useServiceHook(
    customerDetails,
    [custId, country]
  );

  const [customerDetailsState, setCustomerDetailsState] = useState<MappedCustomerDetails | null>(null);

  // Get vehicle and quote data from navigation state

  const { data: onboardingData } = useOnboardingData();

  // AU uses a single plan constant; GB uses plan name lists
  const isUsedVehiclePlan = isAU
    ? onboardingVehicleState.plan === CBPAllowedPlans.SplendRentToOwnUsedVehicle
    : flexiOwnUsedPlanNames.includes(onboardingVehicleState.plan?.toLowerCase()) ||
      preLovedPlanNames.includes(onboardingVehicleState.plan?.toLowerCase());

  useEffect(() => {
    if (!loading && customerDetailsData) {
      const mappedDetails = mapCustomerDetails(customerDetailsData);
      setCustomerDetailsState(mappedDetails);

      // Set default gender if not provided
      const defaultGender = mappedDetails.gender || 'Male';
      // Set default nationality based on country if not provided
      const defaultNationality = mappedDetails.nationality || (isAU ? 'Australian' : 'British');

      onboardingPersonalDispatch({
        type: "SET_ALL",
        payload: {
          firstName: mappedDetails.firstName,
          middleName: mappedDetails.middleName,
          lastName: mappedDetails.lastName,
          email: mappedDetails.email,
          mobile: mappedDetails.mobile,
          gender: defaultGender,
          nationality: defaultNationality,
          // AU-specific fields
          ...(isAU ? {
            hirerABN: mappedDetails.abn || "",
            alternativeName: mappedDetails.alternativeName || "",
          } : {
            // GB-specific fields
            nationalInsuranceNumber: "",
          }),
        }
      });

      // Pre-populate address details from customer data
      onboardingAddressDispatch({
        type: "SET_ALL",
        payload: {
          street: mappedDetails.street,
          city: mappedDetails.city,
          state: mappedDetails.state,
          postcode: mappedDetails.postcode,
          country: country,
        }
      });

      // Pre-populate driver licence details from customer data
      onboardingDriverLicenceDispatch({
        type: "SET_ALL",
        payload: {
          driverLicenceNumber: mappedDetails.driverLicenceNumber,
          licenceExpiry: mappedDetails.licenceExpiry,
          licenceState: mappedDetails.licenceState,
          dob: mappedDetails.dob,
          pcoLicenseNumber: mappedDetails.pcoLicenseNumber,
          pcoLicenceExpiryDate: mappedDetails.pcoLicenceExpiryDate,
        }
      });

      // Pre-populate emergency contacts from customer data
      onboardingEmergencyContactsDispatch({
        type: "SET_ALL",
        payload: {
          primaryName: mappedDetails.primaryEmergencyName,
          primaryPhone: mappedDetails.primaryEmergencyPhone,
          primaryAddress: mappedDetails.primaryEmergencyAddress,
          primaryRelationship: mappedDetails.primaryEmergencyRelationship,
          secondaryName: mappedDetails.secondaryEmergencyName,
          secondaryPhone: mappedDetails.secondaryEmergencyPhone,
          secondaryAddress: mappedDetails.secondaryEmergencyAddress,
          secondaryRelationship: mappedDetails.secondaryEmergencyRelationship,
        }
      });
    }
  }, [loading, customerDetailsData]);

  // Populate vehicle and agreement data from navigation state
  useEffect(() => {
    if (onboardingData?.vehicleData) {
      onboardingVehicleDispatch({
        type: "SET_ALL",
        payload: {
          vehicleId: onboardingData.vehicleData.vehicleId,
          make: onboardingData.vehicleData.make,
          model: onboardingData.vehicleData.model,
          year: onboardingData.vehicleData.year,
          color: onboardingData.vehicleData.color,
          licensePlate: onboardingData.vehicleData.licensePlate,
          vin: onboardingData.vehicleData.vin,
          bodyType: onboardingData.vehicleData.bodyType,
          odometer: onboardingData.vehicleData.odometer,
          hub: onboardingData.vehicleData.hub,
          plan: onboardingData.vehicleData.plan,
        }
      });
    }

    if (onboardingData?.quoteData) {
      // Calculate planWeeklyFee from quoteAddons (total of all included items including GST)
      const planWeeklyFee = getTotalWeeklySubscriptionPrice(onboardingData.quoteData.quoteAddons || []);
      
      // Populate agreement state with quote data including CBPOnboard fields
      onboardingAgreementDispatch({
        type: "SET_ALL",
        payload: {
          potentialId: onboardingData.quoteData.potentialId,
          planName: onboardingData.vehicleData.plan,
          planId: onboardingData.quoteData.planId,
          contactOwnerId: onboardingData.quoteData.contactOwnerId,
          planLen: onboardingData.quoteData.termLength,
          planWeeklyFee: planWeeklyFee,
          planWeeklyDistAllowance: Number(onboardingData.quoteData.weeklyDistanceAllowance),
          planMinimumPeriod: onboardingData.quoteData.minimumTerm,
          planSetupFee: onboardingData.quoteData.setupFeeIncludingGst,
          planInitialContribution: onboardingData.quoteData.initialContribution || "",
          pricingRecordId: onboardingData.quoteData.pricingRecordId,
          quoteAddons: onboardingData.quoteData.quoteAddons || [],
          reducedTermLengthWeeks: onboardingData.quoteData.reducedTermLengthWeeks,
          termReduction: onboardingData.quoteData.termReduction,
          weeklyDistanceAllowance: onboardingData.quoteData.weeklyDistanceAllowance,
          excessDistanceCost: onboardingData.quoteData.excessDistanceCost,
          minimumTerm: onboardingData.quoteData.minimumTerm,
          lossAndDamageWaiverValue: onboardingData.quoteData.lossAndDamageWaiverValue || "",
          a1TotalValue: onboardingData.quoteData.a1TotalValue || "",
          a2TotalValue: onboardingData.quoteData.a2TotalValue || "",
          a3TotalValue: onboardingData.quoteData.a3TotalValue || "",
          totalAddonsValue: onboardingData.quoteData.totalAddonsValue || "",
          vehicleDriveawayPrice: onboardingData.quoteData.vehicleDriveawayPrice || "",
          replacementTyres: onboardingData.quoteData.replacementTyres || "",
          replacementBrakePadSet: onboardingData.quoteData.replacementBrakePadSet || "",
          replacementOrMachiningOfBrakeDiscs: onboardingData.quoteData.replacementOrMachiningOfBrakeDiscs || "",
        }
      });
    }
  }, [onboardingData]);

  function buildOnboardingPath(step: FlexiOwnOnboardingPath): string {
    return `/flexi-own-onboarding-v2/${country}/${custId}/${step}`;
  }

  /**
   * Returns the full navigation path for the step that follows `current` in the
   * onboarding journey. AU and GB journeys share most steps but diverge at
   * AgreementSchedule (AU inserts UsedVehicleAddendum / NSWEVAddendum before
   * InsurancePriceBeat).
   */
  function getNextPath(current: FlexiOwnOnboardingPath): string {
    switch (current) {
      case FlexiOwnOnboardingPath.Root:
        return buildOnboardingPath(FlexiOwnOnboardingPath.UberProof);
      case FlexiOwnOnboardingPath.UberProof:
        return buildOnboardingPath(FlexiOwnOnboardingPath.PersonalDetails);
      case FlexiOwnOnboardingPath.PersonalDetails:
        return buildOnboardingPath(FlexiOwnOnboardingPath.AddressDetails);
      case FlexiOwnOnboardingPath.AddressDetails:
        return buildOnboardingPath(FlexiOwnOnboardingPath.DriverLicence);
      case FlexiOwnOnboardingPath.DriverLicence:
        return buildOnboardingPath(FlexiOwnOnboardingPath.EmergencyContacts);
      case FlexiOwnOnboardingPath.EmergencyContacts:
        return buildOnboardingPath(FlexiOwnOnboardingPath.AgreementSchedule);
      case FlexiOwnOnboardingPath.AgreementSchedule:
        // AU branches to UsedVehicleAddendum or NSWEVAddendum; GB goes straight to InsurancePriceBeat
        if (isAU) {
          return isUsedVehiclePlan
            ? buildOnboardingPath(FlexiOwnOnboardingPath.UsedVehicleAddendum)
            : buildOnboardingPath(FlexiOwnOnboardingPath.NSWEVAddendum);
        }
        return buildOnboardingPath(FlexiOwnOnboardingPath.InsurancePriceBeat);
      case FlexiOwnOnboardingPath.UsedVehicleAddendum:
        // AU continues to NSWEVAddendum; GB goes straight to Review
        return isAU
          ? buildOnboardingPath(FlexiOwnOnboardingPath.NSWEVAddendum)
          : buildOnboardingPath(FlexiOwnOnboardingPath.Review);
      case FlexiOwnOnboardingPath.NSWEVAddendum:
        return buildOnboardingPath(FlexiOwnOnboardingPath.InsurancePriceBeat);
      case FlexiOwnOnboardingPath.InsurancePriceBeat:
        // GB branches to UsedVehicleAddendum here; AU has already handled this after AgreementSchedule
        if (!isAU && isUsedVehiclePlan) {
          return buildOnboardingPath(FlexiOwnOnboardingPath.UsedVehicleAddendum);
        }
        return buildOnboardingPath(FlexiOwnOnboardingPath.Review);
      case FlexiOwnOnboardingPath.Review:
        return buildOnboardingPath(FlexiOwnOnboardingPath.InvoicePreview);
      case FlexiOwnOnboardingPath.InvoicePreview:
        return "/";
    }
  }

  // Validate all required states up to and including the current route
  function validateJourneyState(window_path: string): string | null {
    // First step, so this checks prior dependencies rather than any state captured during this FlexiOwn flow.
    if (window_path.endsWith(`/${FlexiOwnOnboardingPath.UberProof}`)) {
      if (!onboardingAgreementState.potentialId )
        return "No potential found.";
    }

    if (
      window_path.endsWith(`/${FlexiOwnOnboardingPath.AddressDetails}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.DriverLicence}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.EmergencyContacts}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.AgreementSchedule}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.InsurancePriceBeat}`)
    ) {
      if (!onboardingPersonalState.firstName || onboardingPersonalState.firstName.trim().length === 0)
        return "No personal details found.";
      if (!onboardingPersonalState.email || onboardingPersonalState.email.trim().length === 0)
        return "No email found.";
    }

    // Check address details for all steps after address-details
    if (
      window_path.endsWith(`/${FlexiOwnOnboardingPath.DriverLicence}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.EmergencyContacts}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.AgreementSchedule}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.InsurancePriceBeat}`)
    ) {
      if (!onboardingAddressState.postcode || onboardingAddressState.postcode.trim().length === 0)
        return "No address details found.";
    }

    // Check driver licence for all steps after driver-licence
    if (
      window_path.endsWith(`/${FlexiOwnOnboardingPath.EmergencyContacts}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.AgreementSchedule}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.InsurancePriceBeat}`)
    ) {
      if (!onboardingDriverLicenceState.driverLicenceNumber || onboardingDriverLicenceState.driverLicenceNumber.trim().length === 0)
        return "No driver licence details found.";
    }

    // Check emergency contacts for all steps after emergency-contacts
    if (
      window_path.endsWith(`/${FlexiOwnOnboardingPath.AgreementSchedule}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.InsurancePriceBeat}`)
    ) {
      if (!onboardingEmergencyContactsState.primaryName || onboardingEmergencyContactsState.primaryName.trim().length === 0)
        return "No emergency contact details found.";
    }

    // Check agreement schedule for all steps after agreement-schedule
    if (
      window_path.endsWith(`/${FlexiOwnOnboardingPath.InsurancePriceBeat}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.UsedVehicleAddendum}`) ||
      window_path.endsWith(`/${FlexiOwnOnboardingPath.Review}`)
    ) {
      if (!onboardingAgreementState.planId || onboardingAgreementState.planId.trim().length === 0)
        return "No agreement schedule found.";
    }

    return null;
  }

  // Validate state is set, when the path changes
  useEffect(() => {
    const error = validateJourneyState(window.location.hash);
    if (error) {
      setErrorMessage(error);
      setShowErrorModal(true);
    } else {
      setShowErrorModal(false);
      setErrorMessage("");
    }
  }, [window.location.pathname, onboardingAgreementState.potentialId]);

  // If state error, sends the user back to the start of the journey (or SmartOps home if necessary)
  function handleStateErrorModalClose() {
    setShowErrorModal(false);
    if (!onboardingAgreementState.potentialId) {
      navigate("/");
    } else {
      navigate(buildOnboardingPath(FlexiOwnOnboardingPath.Root));
    }
  }

  return (
    <>
      {showErrorModal && (
        <Warning
          message={errorMessage}
          additionalInfo={"The Flexi Own Onboarding Journey has encountered an error. Missing state from a previous step. You must restart the journey."}
          onClickClose={handleStateErrorModalClose}
        />
      )}
      <Routes>
        <Route
          path={FlexiOwnOnboardingPath.Root}
          element={<Navigate to={getNextPath(FlexiOwnOnboardingPath.Root)} replace />}
        />
        <Route
          path={FlexiOwnOnboardingPath.UberProof}
          element={
            <FlexiOwnUberProofV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.UberProof)}
              custId={custId || ""}
              country={country}
              potentialId={onboardingAgreementState.potentialId}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.PersonalDetails}
          element={
            <FlexiOwnPersonalDetailsV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.PersonalDetails)}
              onboardingPersonalState={onboardingPersonalState}
              onboardingPersonalDispatch={onboardingPersonalDispatch}
              country={country}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.AddressDetails}
          element={
            <FlexiOwnAddressDetailsV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.AddressDetails)}
              onboardingAddressState={onboardingAddressState}
              onboardingAddressDispatch={onboardingAddressDispatch}
              country={country}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.DriverLicence}
          element={
            <FlexiOwnDriverLicenceV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.DriverLicence)}
              onboardingDriverLicenceState={onboardingDriverLicenceState}
              onboardingDriverLicenceDispatch={onboardingDriverLicenceDispatch}
              country={country}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.EmergencyContacts}
          element={
            <FlexiOwnEmergencyContactsV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.EmergencyContacts)}
              onboardingEmergencyContactsState={onboardingEmergencyContactsState}
              onboardingEmergencyContactsDispatch={onboardingEmergencyContactsDispatch}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.AgreementSchedule}
          element={
            <FlexiOwnAgreementScheduleV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.AgreementSchedule)}
              onboardingAgreementState={onboardingAgreementState}
              onboardingAgreementDispatch={onboardingAgreementDispatch}
              country={country}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.UsedVehicleAddendum}
          element={
            <FlexiOwnUsedVehicleAddendumV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.UsedVehicleAddendum)}
              onboardingAgreementState={onboardingAgreementState}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.NSWEVAddendum}
          element={
            <FlexiOwnNSWEVAddendumV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.NSWEVAddendum)}
              onboardingAgreementState={onboardingAgreementState}
              onboardingAgreementDispatch={onboardingAgreementDispatch}
              custId={custId || ""}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.InsurancePriceBeat}
          element={
            <FlexiOwnInsurancePriceBeatV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.InsurancePriceBeat)}
              onboardingInsuranceState={onboardingInsuranceState}
              onboardingInsuranceDispatch={onboardingInsuranceDispatch}
              onboardingPersonalState={onboardingPersonalState}
              onboardingAddressState={onboardingAddressState}
              onboardingDriverLicenceState={onboardingDriverLicenceState}
              onboardingEmergencyContactsState={onboardingEmergencyContactsState}
              onboardingAgreementState={onboardingAgreementState}
              country={country}
              custId={custId || ""}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.Review}
          element={
            <FlexiOwnOnboardingReviewV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.Review)}
              onboardingPersonalState={onboardingPersonalState}
              onboardingAddressState={onboardingAddressState}
              onboardingDriverLicenceState={onboardingDriverLicenceState}
              onboardingEmergencyContactsState={onboardingEmergencyContactsState}
              onboardingAgreementState={onboardingAgreementState}
              onboardingInsuranceState={onboardingInsuranceState}
              onboardingVehicleState={onboardingVehicleState}
              country={country}
              custId={custId || ""}
            />
          }
        />
        <Route
          path={FlexiOwnOnboardingPath.InvoicePreview}
          element={
            <InvoicePreviewV2
              navigateTarget={getNextPath(FlexiOwnOnboardingPath.InvoicePreview)}
              onboardingPersonalState={onboardingPersonalState}
              onboardingAddressState={onboardingAddressState}
              onboardingDriverLicenceState={onboardingDriverLicenceState}
              onboardingEmergencyContactsState={onboardingEmergencyContactsState}
              onboardingAgreementState={onboardingAgreementState}
              onboardingInsuranceState={onboardingInsuranceState}
              onboardingVehicleState={onboardingVehicleState}
              country={country}
              custId={custId || ""}
            />
          }
        />
      </Routes>
      <Outlet />
    </>
  );
}

// Map API response to clean customer details state
export interface MappedCustomerDetails {
  // Personal details
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  dob: string;
  gender: string;
  mobile: string;
  nationality: string;

  // Address details
  street: string;
  city: string;
  state: string;
  province: string;
  postcode: string;

  // Driver licence details
  driverLicenceNumber: string;
  licenceExpiry: string;
  licenceState: string;
  pcoLicenseNumber: string;
  pcoLicenceExpiryDate: string;

  // Emergency contact details
  primaryEmergencyName: string;
  primaryEmergencyPhone: string;
  primaryEmergencyAddress: string;
  primaryEmergencyRelationship: string;
  secondaryEmergencyName: string;
  secondaryEmergencyPhone: string;
  secondaryEmergencyAddress: string;
  secondaryEmergencyRelationship: string;

  // Other details
  abn: string;
  alternativeName: string;
  plan: string;
  memberStatus: string;
  identityVerificationStatus: string;
  blacklisted: boolean;
}

export function mapCustomerDetails(apiResponse: any): MappedCustomerDetails {
  const data = apiResponse.data;

  return {
    // Personal details
    firstName: data.first_name || '',
    middleName: data.middle_name || '',
    lastName: data.last_name || '',
    email: data.email || '',
    dob: data.dob || '',
    gender: data.gender || '',
    mobile: data.mobile || '',
    nationality: data.nationality || '',

    // Address details
    street: data.street || '',
    city: data.city || '',
    state: data.state || '',
    province: data.province || '',
    postcode: data.postcode || '',

    // Driver licence details
    driverLicenceNumber: data.driver_licence_number || '',
    licenceExpiry: data.licence_expiry || '',
    licenceState: data.licence_state || '',
    pcoLicenseNumber: data.PCO_License_Number || '',
    pcoLicenceExpiryDate: data.PCO_License_Expiry_Date || '',

    // Emergency contact details
    primaryEmergencyName: data.primary_emergency_name || '',
    primaryEmergencyPhone: data.primary_emergency_phone || '',
    primaryEmergencyAddress: data.primary_emergency_address || '',
    primaryEmergencyRelationship: data.primary_emergency_relationship || '',
    secondaryEmergencyName: data.secondary_emergency_name || '',
    secondaryEmergencyPhone: data.secondary_emergency_phone || '',
    secondaryEmergencyAddress: data.secondary_emergency_address || '',
    secondaryEmergencyRelationship: data.secondary_emergency_relationship || '',

    // Other details
    abn: data.abn || '',
    alternativeName: data.alternative_name || '',
    plan: data.Plan || '',
    memberStatus: data.member_status || '',
    identityVerificationStatus: data.identity_verification_status || '',
    blacklisted: data.blacklisted || false,
  };
}